import React from "react"
import { useNavigate } from "react-router-dom"
import { reportShowPath } from "@/QuarterlyReportApp"
import { ArrowLeft } from "lucide-react"
import { ReportSection } from "~/api/communications"
import { Button } from "@/ui/Button"
import QuarterlyReportLayout from "./QuarterlyReportLayout"
import { useCommunicationContext } from "./CommunicationContext"
import { useQuarterlyReportContext } from "./useQuarterlyReportContext"
import { usePdfView } from "./usePdfView"
import logoName from "images/logo_name.svg"

const CoverLetter: React.FC = () => {
  const { communicationId, communication } = useCommunicationContext()
  const { reportId, report, isLoading, error, mock } =
    useQuarterlyReportContext()

  const { showPdfView, pdfLoading, pdfUrl, togglePdfView, loadPdfView } =
    usePdfView({
      reportId,
      sectionName: ReportSection.COVER_LETTER,
      editorContent: { section: "cover_letter", reportId },
      mock,
    })

  const navigate = useNavigate()

  const formatTitle = () => {
    if (communication?.year && communication?.quarter) {
      return `${report?.household_name} - Q${communication.quarter} ${communication.year}`
    }
    return report?.household_name || "Cover Letter"
  }

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Loading Cover Letter...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Failed to load report: {error.message}
      </div>
    )
  }

  if (!report) {
    return (
      <div className="alert alert-warning" role="alert">
        Report not found
      </div>
    )
  }

  return (
    <QuarterlyReportLayout
      title="Cover Letter"
      subtitle={report.household_name}
      showEditorActions={true}
      reportId={reportId}
      showPdfView={showPdfView}
      pdfLoading={pdfLoading}
      pdfUrl={pdfUrl}
      onTogglePdfView={togglePdfView}
      actions={
        <Button
          size="md"
          variant="outline"
          icon={<ArrowLeft size={18} />}
          onClick={() =>
            navigate(reportShowPath({ communicationId, reportId }))
          }
        >
          Back to Report
        </Button>
      }
      breadcrumbProps={{
        householdName: report.household_name,
        householdPath: reportShowPath({ communicationId, reportId }),
        sectionName: ReportSection.COVER_LETTER,
      }}
    >
      <div className="pdf-editor-container">
        <div className="pdf-dimensions-helper">
          Letter size (8.5 inches) -{" "}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault()
              loadPdfView()
            }}
            style={{ textDecoration: "underline", cursor: "pointer" }}
          >
            PDF View
          </a>
        </div>
        <div className="pdf-document">
          <div className="pdf-content cover-letter-content">
            <img src={logoName} alt="Quadrant" className="cover-letter-logo" />
            <h1 className="cover-letter-title">{formatTitle()}</h1>
          </div>
        </div>
      </div>
    </QuarterlyReportLayout>
  )
}

export default CoverLetter
